import type { MediaLibraryListParams } from "@/types/catalogAssets";
import type {
  CatalogProductGroup,
  ProductCardsTaxonomyFilter,
} from "@/types/ingestedProduct";

import {
  isPilotFlowGroupType,
  taxonomyFilterForGroup,
  type PilotFlowGroupType,
} from "@/features/Generations/pilotFlowGroup";

/** Which taxonomy a side-nav entry represents. */
export type SKUStoreCollectionType = "all" | PilotFlowGroupType;

/** One selectable entry in the SKU Store landing side nav. */
export type SKUStoreCollection = {
  /** Stable id used for selection and render keys; unique across taxonomies. */
  id: string;
  /** Display name shown in the side nav and collection view (the handle). */
  name: string;
  type: SKUStoreCollectionType;
  /** Taxonomy handle passed to the product-cards filter (absent for "all"). */
  handle?: string;
  productCount?: number;
  groupId?: number;
};

/** Sentinel id for the pinned "All Products" entry (not a real collection). */
export const ALL_PRODUCTS_COLLECTION_ID = "__all_products__";
export const ALL_PRODUCTS_COLLECTION_NAME = "All Products";

export const ALL_PRODUCTS_COLLECTION: SKUStoreCollection = {
  id: ALL_PRODUCTS_COLLECTION_ID,
  name: ALL_PRODUCTS_COLLECTION_NAME,
  type: "all",
};

/**
 * Map the handles/counts returned by the collection or category list endpoint
 * into side-nav entries. The handle doubles as the display name and the filter
 * value; the id is namespaced by type so a collection and category sharing a
 * handle stay distinct in the nav.
 */
export function toCollectionEntries(
  groups: CatalogProductGroup[],
  type: PilotFlowGroupType,
): SKUStoreCollection[] {
  return groups.map((group) => ({
    id: `${type}:${group.handle}`,
    name: group.handle,
    type,
    handle: group.handle,
    productCount: group.product_count,
    groupId: group.id,
  }));
}

export function collectionFromHandle(
  type: "collection" | "category" | "tag",
  handle: string,
): SKUStoreCollection {
  return { id: `${type}:${handle}`, name: handle, type, handle };
}

/** Landing-page search params that select a side-nav entry. */
export type SKUStoreCollectionSearch = {
  collection?: string;
  category?: string;
  tag?: string;
};

/** Resolve the selected side-nav entry from the URL; none set means All Products. */
export function collectionFromSearch({
  collection,
  category,
  tag,
}: SKUStoreCollectionSearch): SKUStoreCollection {
  if (collection) return collectionFromHandle("collection", collection);
  if (category) return collectionFromHandle("category", category);
  if (tag) return collectionFromHandle("tag", tag);
  return ALL_PRODUCTS_COLLECTION;
}

/** Search params for selecting `collection`; clears the other taxonomies. */
export function searchForCollection(
  collection: SKUStoreCollection,
): SKUStoreCollectionSearch {
  const handleFor = (type: SKUStoreCollectionType) =>
    collection.type === type ? collection.handle : undefined;
  return {
    collection: handleFor("collection"),
    category: handleFor("category"),
    tag: handleFor("tag"),
  };
}

export function taxonomyFilterForCollection(
  collection: SKUStoreCollection,
): ProductCardsTaxonomyFilter | undefined {
  if (!collection.handle || !isPilotFlowGroupType(collection.type)) {
    return undefined;
  }
  return taxonomyFilterForGroup(collection.type, collection.handle);
}

export function mediaLibraryFilterForCollection(
  collection: SKUStoreCollection,
): ProductCardsTaxonomyFilter | undefined {
  return taxonomyFilterForCollection(collection);
}

export const MEDIA_TAB_LIBRARY_PARAMS = {
  approval_status: ["approved"],
  type: ["image", "video"],
  sort_by: "newest",
} as const satisfies MediaLibraryListParams;

export type PilotFlowLaunchSearch =
  | { allProducts: true; groupName: string }
  | { groupId?: number; groupType: PilotFlowGroupType; groupName: string }
  | Record<string, never>;

export function pilotFlowSearchForCollection(
  collection: SKUStoreCollection,
): PilotFlowLaunchSearch {
  if (collection.type === "all")
    return { allProducts: true, groupName: collection.name };
  if (collection.handle && isPilotFlowGroupType(collection.type)) {
    return {
      groupId: collection.groupId,
      groupType: collection.type,
      groupName: collection.name,
    };
  }
  return {};
}
